import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Wallet, Zap, CalendarClock } from "lucide-react";
import { EDITORIAL_IMAGES } from "../lib/editorialImages";

const HERO_IMG = EDITORIAL_IMAGES.partner;

const BENEFITS = [
  {
    icon: Wallet,
    k: "No upfront cost",
    v: "Join free and list straight away. Nothing is charged until your trial ends — you only pay once FYLLR is quietly filling your quiet hours.",
  },
  {
    icon: Zap,
    k: "Keep 100% of every booking",
    v: "FYLLR takes zero commission. Clients pay you directly, in your salon, your way — we simply send them through the door.",
  },
  {
    icon: CalendarClock,
    k: "Smart session management",
    v: "Post last-minute openings and recurring, capacity-based sessions in seconds. FYLLR handles reminders, capacity and no-shows for you.",
  },
];

export default function ForSalons() {
  return (
    <div className="bg-espresso text-bone min-h-screen" data-testid="for-salons-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] min-h-[88vh]">
          <div className="flex items-center px-6 md:px-12 lg:px-16 pt-24 lg:pt-16 pb-16">
            <div className="max-w-xl">
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-[11px] tracking-[0.3em] uppercase text-mocha"
              >
                FYLLR for Partners
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.06 }}
                className="mt-6 font-serif text-5xl md:text-7xl leading-[0.92] text-bone"
              >
                Fill your gaps,<br />beautifully.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.12 }}
                className="mt-7 text-base md:text-lg text-bone/70 leading-relaxed max-w-md"
              >
                Every cancellation is a quiet hour you never planned for. FYLLR turns those
                last-minute openings into booked appointments — with clients who want exactly
                what you do best, right now.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                className="mt-10 flex flex-wrap items-center gap-4"
              >
                <Link
                  to="/salon/onboard"
                  className="inline-flex items-center gap-2 rounded-full bg-bone text-ink px-8 py-4 text-sm hover:bg-sand transition-colors"
                  data-testid="for-salons-hero-cta"
                >
                  Join FYLLR — it’s free <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 rounded-full border border-bone/30 px-8 py-4 text-sm text-bone/75 hover:border-mocha hover:text-bone transition-colors"
                  data-testid="for-salons-hero-pricing"
                >
                  See pricing
                </Link>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            className="relative hidden lg:block"
          >
            <img
              src={HERO_IMG}
              alt="Vogue-style FYLLR partner beauty editorial"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="for-salons-hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-espresso via-espresso/10 to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* BENEFIT COLUMNS */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <motion.h2
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          className="font-serif text-4xl md:text-5xl text-bone max-w-2xl leading-[0.98]"
        >
          Built for the way your diary really works.
        </motion.h2>

        <div className="mt-14 grid md:grid-cols-3 gap-x-12 gap-y-14">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.k}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.08 }}
                data-testid={`for-salons-benefit-${i}`}
              >
                <span className="inline-flex w-11 h-11 rounded-full bg-bone/10 items-center justify-center text-mocha">
                  <Icon size={19} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-serif text-2xl md:text-3xl text-bone leading-tight">{b.k}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-bone/65">{b.v}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-ink text-bone">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
            <div>
              <p className="text-[11px] tracking-[0.3em] uppercase text-mocha">Honest, flat pricing</p>
              <h2 className="mt-5 font-serif text-5xl md:text-6xl leading-[0.95]">
                $38.50<span className="font-sans text-base text-bone/55"> / month · inc GST</span>
              </h2>
              <p className="mt-6 text-lg text-bone/75 leading-relaxed max-w-md">
                Join before <strong className="text-bone">7 February 2027</strong> and there is no
                charge before <strong className="text-bone">7 April 2027</strong> — two launch months
                completely on us.
              </p>
              <p className="mt-4 text-[15px] text-bone/55 max-w-md">
                No commission. No lock-in. Cancel anytime before your first charge and you pay nothing at all.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5">
                <Link
                  to="/salon/onboard"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-bone text-ink px-8 py-4 text-sm hover:bg-sand transition-colors"
                  data-testid="for-salons-pricing-cta"
                >
                  Join FYLLR — it’s free <ArrowRight size={15} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/salon/login"
                  className="text-sm text-bone/60 hover:text-bone underline underline-offset-4 decoration-mocha/60 transition-colors"
                  data-testid="for-salons-signin"
                >
                  Already a partner? Sign in
                </Link>
              </div>
            </div>

            <ul className="space-y-4 lg:border-l lg:border-bone/15 lg:pl-12">
              {["Card added now — $0 charged before 7 April 2027",
                "Keep 100% — clients pay you directly, no commission",
                "Unlimited last-minute openings & sessions",
                "Editorial venue profile on the marketplace",
                "Clients always book free"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-[15px] text-bone/80">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-mocha shrink-0" />{t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
